import Button from "components/buttons/Button";
import { Modal } from "components/Modal/Modal";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import {
  getPartnerItem,
  handleClearDraftItem,
  updatePartnerItem,
  createPartnerItem,
} from "store/cms/footer/partners/slice";
import { closeModal } from "store/modals/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ModalTypes } from "configs/types";
import { selectFilteredGeo } from "store/geoFilter/selectors";

import styles from "../partners/CmsFooterPartnersEditModal.module.scss";

import PartnersMainTab from "./tabs/PartnersMainTab";
import PartnersTrailTab from "./tabs/PartnersTrailTab";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";

enum ModalPartnersTabs {
  main = "main",
  trail = "trail",
}

const CmsFooterPartnersEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const entityId = searchParams.get("entityId") ?? "";
  const isEdit = Boolean(entityId);
  const formErrors = useFormErrors();

  const isItemFetched = useAppSelector(
    (store) => store["cms-partners"].isItemFetched,
  );
  const isItemFetching = useAppSelector(
    (store) => store["cms-partners"].isItemFetching,
  );
  const geoList = useAppSelector(selectFilteredGeo);
  const draftItem = useAppSelector((store) => store["cms-partners"].draftItem);

  const [activeTab, setActiveTab] = useState<ModalPartnersTabs>(
    ModalPartnersTabs.main,
  );

  const newUploadedImages = useRef<{ id: number; image: File }[]>([]);
  
  const permissions = usePermissionHelper();

  useEffect(() => {
    if (entityId && !isItemFetched) {
      dispatch(getPartnerItem(+entityId));
    }
  }, [entityId, isItemFetched]);

  const isLoaded = () => {
    if (entityId && !isItemFetched) return false;
    return true;
  };
  const loaded = isLoaded();

  const handleClose = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearDraftItem());
  };

  const handleSave = async () => {
    const { items, geo, ...rest } = draftItem;

    const bannersData = items.map((item) => {
      const newImage = newUploadedImages.current.find((i) => i.id === item.id);

      return {
        id: item.id,
        href: item.href,
        priority: item.priority,
        alt: item.alt,
        ...(newImage && { image: newImage.image }),
      };
    });

    const payload = {
      ...rest,
      geo: geo || geoList[0],
      items: bannersData,
    };

    formErrors.resetErrors();

    try {
      if (entityId) {
        await dispatch(
          updatePartnerItem({ ...payload, id: +entityId }),
        ).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } else {
        const result = await dispatch(createPartnerItem(payload)).unwrap();

        navigate(
          `${location.pathname}?activeModal=${ModalTypes.partner}&entityId=${result.id}`,
        );

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      }
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
    }
  };

  const canBeSaved = () => {
    const hasModifiedFields = draftItem.isModified;
    const hasModifiedBanners = draftItem.items.some((b) => b.isModified);

    if (!entityId) return hasModifiedFields && hasModifiedBanners;
    return hasModifiedFields || hasModifiedBanners;
  };

  const handleTabClick = (tab: ModalPartnersTabs) => {
    if (!isEdit && tab !== ModalPartnersTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
  };

  const renderTab = () => {
    if (activeTab === ModalPartnersTabs.main) {
      return (
        <PartnersMainTab
          newUploadedImages={newUploadedImages}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          resetErrors={formErrors.resetErrors}
          readOnly={!permissions?.update}
        />
      );
    }

    if (activeTab === ModalPartnersTabs.trail) {
      return <PartnersTrailTab entityId={entityId!} />;
    }

    return null;
  };

  return (
    <Modal onClose={handleClose}>
      <Modal.Header>{t("partners")}</Modal.Header>

      {loaded && (
        <Modal.Tabs
          tabs={[ModalPartnersTabs.main, ModalPartnersTabs.trail]}
          activeTab={activeTab}
          onClick={handleTabClick}
          canBeClicked={isItemFetched}
        />
      )}

      <Modal.Content className={styles.cmsFooterPartnersWrapper}>
        {!loaded ? <ModalSkeleton /> : renderTab()}
      </Modal.Content>

      {loaded && activeTab !== ModalPartnersTabs.trail && permissions?.update && (
        <Modal.Footer>
          <Button isSecondary txt={t("cancel")} handleClick={handleClose} />
          <Button
            isPrimary
            handleClick={handleSave}
            txt={t("save")}
            isLoad={isItemFetching}
            disabled={!canBeSaved()}
          />
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default CmsFooterPartnersEditModal;
